/**
 * CR316: Search Dropdown Panel
 * 
 * Unified dropdown for search suggestions and recent searches.
 * Shows recent searches when input is empty + focused.
 * Shows suggestions when input has text >= minChars.
 * CRITICAL: Only uses card-visible fields for suggestions (no terms/fine print).
 */

import { useMemo } from "react";
import { Clock, Search, Store, Tag, Grid2X2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { SearchSuggestionsConfig, RecentSearchesConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_SUGGESTIONS_CONFIG, DEFAULT_RECENT_SEARCHES_CONFIG } from "@/lib/layout-studio/types";

export interface SuggestionItem {
  text: string;
  type: "merchant" | "brand" | "category" | "title";
  count?: number;
}

interface SearchDropdownPanelProps {
  /** Current search input value */
  searchQuery: string;
  /** Whether the search input is focused */
  isFocused: boolean;
  /** Search suggestions config */
  suggestionsConfig?: SearchSuggestionsConfig;
  /** Recent searches config */
  recentSearchesConfig?: RecentSearchesConfig;
  /** Generated suggestions based on current deals */
  suggestions: SuggestionItem[];
  /** Recent search terms */
  recentSearches: string[];
  /** Callback when a suggestion or recent is selected */
  onSelect: (text: string) => void;
  /** Callback to clear all recent searches */
  onClearRecents?: () => void;
  /** Callback to remove a single recent search */
  onRemoveRecent?: (text: string) => void;
}

const TYPE_ICONS: Record<SuggestionItem["type"], React.ReactNode> = {
  merchant: <Store className="h-3 w-3" />,
  brand: <Tag className="h-3 w-3" />,
  category: <Grid2X2 className="h-3 w-3" />,
  title: <Search className="h-3 w-3" />,
};

const TYPE_LABELS: Record<SuggestionItem["type"], string> = {
  merchant: "Merchant",
  brand: "Brand",
  category: "Category",
  title: "Deal",
};

export function SearchDropdownPanel({
  searchQuery,
  isFocused,
  suggestionsConfig,
  recentSearchesConfig,
  suggestions,
  recentSearches,
  onSelect,
  onClearRecents,
  onRemoveRecent,
}: SearchDropdownPanelProps) {
  const suggConfig = { ...DEFAULT_SEARCH_SUGGESTIONS_CONFIG, ...suggestionsConfig };
  const recentConfig = { ...DEFAULT_RECENT_SEARCHES_CONFIG, ...recentSearchesConfig };

  // Determine what to show
  const trimmedQuery = searchQuery.trim();
  const showRecents = 
    recentConfig.enabled &&
    recentConfig.showWhenFocused &&
    isFocused &&
    (recentConfig.showWhenEmpty ? trimmedQuery.length === 0 : false) &&
    recentSearches.length > 0;

  const showSuggestions = 
    suggConfig.enabled &&
    isFocused &&
    trimmedQuery.length >= suggConfig.minChars &&
    suggestions.length > 0;

  // Filter and limit suggestions
  const displaySuggestions = useMemo(() => {
    if (!showSuggestions) return [];
    
    let filtered = suggestions.filter((s) => suggConfig.typesEnabled[s.type]);
    
    // Dedupe if enabled
    if (suggConfig.dedupe) {
      const seen = new Set<string>();
      filtered = filtered.filter((s) => {
        const key = s.text.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    
    return filtered.slice(0, suggConfig.maxItems);
  }, [showSuggestions, suggestions, suggConfig]);

  // If nothing to show, return null
  if (!showRecents && !showSuggestions) {
    return null;
  }

  return (
    <div 
      className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border rounded-lg shadow-lg overflow-hidden"
      onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
    >
      {/* Recent Searches */}
      {showRecents && (
        <div className="p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Recent Searches
            </span>
            {recentConfig.showClearAll && onClearRecents && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-[10px] text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClearRecents();
                }}
              >
                Clear All
              </Button>
            )}
          </div>
          <div className="space-y-1">
            {recentSearches.slice(0, recentConfig.maxItems).map((term) => (
              <div
                key={term}
                className="flex items-center justify-between group hover:bg-accent rounded-md px-2 py-1.5 cursor-pointer"
                onClick={() => onSelect(term)}
              >
                <span className="text-sm truncate flex-1">{term}</span>
                {onRemoveRecent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveRecent(term);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {showSuggestions && (
        <div className="p-2">
          {showRecents && <div className="border-t mb-2" />}
          <div className="space-y-1">
            {displaySuggestions.map((suggestion, idx) => (
              <div
                key={`${suggestion.type}-${suggestion.text}-${idx}`}
                className="flex items-center gap-2 hover:bg-accent rounded-md px-2 py-1.5 cursor-pointer"
                onClick={() => onSelect(suggestion.text)}
              >
                {suggConfig.showTypeLabel && (
                  <span className="text-muted-foreground">
                    {TYPE_ICONS[suggestion.type]}
                  </span>
                )}
                <span className="text-sm truncate flex-1">{suggestion.text}</span>
                {suggConfig.showTypeLabel && (
                  <Badge variant="secondary" className="text-[9px] px-1 shrink-0">
                    {TYPE_LABELS[suggestion.type]}
                  </Badge>
                )}
                {suggConfig.showCounts && suggestion.count !== undefined && (
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    ({suggestion.count})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
